"use client";

import { Download, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BeginYourJourney() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const headlineWords = [{
    text: "Your",
    italic: false
  }, {
    text: "radiant",
    italic: true
  }, {
    text: "transformation",
    italic: false
  }, {
    text: "begins",
    italic: false
  }, {
    text: "with",
    italic: false
  }, {
    text: "a",
    italic: false
  }, {
    text: "consultation.",
    italic: true
  }];
  const contactItems = [{
    icon: "phone",
    label: "+1 (555) 246-8101"
  }, {
    icon: "mail",
    label: "hello@joanneglow.com"
  }, {
    icon: "pin",
    label: "122 Beauty Lane, Rose District"
  }];
  return <section data-section-id="2812" id="begin-your-journey" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden="true" style={{
      backgroundImage: "repeating-radial-gradient(circle at 50% 120%, var(--foreground) 0, var(--foreground) 1px, transparent 1px, transparent 60px)"
    }} />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-12 lg:pr-0">
        {}
        <div className="flex flex-col">
          <span className="font-default text-[11px] uppercase tracking-[0.25em] text-chart-4 mb-8">
            — Reserve your artistry
          </span>

          <h2 ref={headlineRef} className="font-default text-foreground font-light leading-[1.05] tracking-tight text-5xl sm:text-6xl lg:text-[4.5rem] max-w-xl mb-8">
            {headlineWords.map((word, i) => <span data-index={i} key={i} className="inline-block transition-all duration-700 ease-out" style={{
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: `${i * 80}ms`
          }}>
                {word.italic ? <em className="font-default italic text-primary">{word.text}</em> : word.text}
                {i < headlineWords.length - 1 && "\u00A0"}
              </span>)}
          </h2>

          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg mb-10">
            Whether you are preparing for your wedding day or a high-fashion editorial shoot, 
            Joanne Glow Studio provides bespoke makeup artistry designed to enhance your natural beauty with precision and elegance.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-8 h-12 font-default text-base shadow-md">
              <Link to="/GetInTouchPage">Schedule a Private Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-lg border-foreground/40 bg-transparent text-foreground hover:bg-foreground/10 hover:text-foreground px-8 h-12 font-default">
              <Link to="/BridalSuitePage">
                <Download className="size-4" />
                Download Bridal Rates
              </Link>
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-muted-foreground">
            {contactItems.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2">
                {item.icon === "phone" && <Phone className="size-3" />}
                {item.icon === "mail" && <Mail className="size-3" />}
                {item.icon === "pin" && <MapPin className="size-3" />}
                <span className="font-default text-[11px] uppercase tracking-[0.18em]">
                  {item.label}
                </span>
                {i < contactItems.length - 1 && <span className="ml-1 inline-block size-1 rounded-lg bg-muted-foreground/50" aria-hidden="true" />}
              </div>)}
          </div>
        </div>

        {}
        <div ref={imageRef} className="relative w-full transition-all duration-1000 ease-out lg:-mr-0" style={{
        opacity: imageVisible ? 1 : 0,
        transform: imageVisible ? "translateY(0)" : "translateY(40px)"
      }}>
          <div className="relative overflow-hidden rounded-lg shadow-lg">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/bridal_makeup_editorial_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/bridal_makeup_editorial_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/bridal_makeup_editorial_portrait.png-wvc-sizes" alt="Flawless bridal makeup look with soft rose-gold tones and editorial lighting" loading="lazy" decoding="async" className="w-full h-[420px] sm:h-[520px] lg:h-[640px] object-cover" />
            {}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
            background: "radial-gradient(ellipse 40% 25% at 45% 55%, hsl(25 40% 65% / 0.25), transparent 70%)"
          }} />
            <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
            background: "linear-gradient(to bottom, transparent 60%, hsl(30 10% 15% / 0.4) 100%)"
          }} />
          </div>
        </div>
      </div>
    </section>;
}